// @flow
import { colors } from 'bench-style-constants';
import React from 'react';
import ReactSelect, { components } from 'react-select';
import { rgba } from 'color-helpers';
import styled from 'styled-components';

import { Icon, iconPaths } from 'bench-internal-theme';

/**
 * Represents the variances between the two differently sized combo boxes in the internal component
 * library.
 */
const sizes = {
  small: {
    buttonPadding: '3px',
    height: '24px',
  },
  medium: {
    buttonPadding: '8px',
    height: '32px',
  },
};

const clearIconURL = encodeURIComponent(`
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="${colors.dust}" d="${iconPaths.close}" />
  </svg>
`);

const ClearButton = styled.button`
  background: ${colors.sand} url('data:image/svg+xml;utf8,${clearIconURL}') 50% 50% no-repeat;
  border: 0 none;
  border-radius: 8px;
  box-shadow: none;
  cursor: pointer;
  display: inline-block;
  height: 16px;
  outline: none;
  padding: 0;
  width: 16px;
`;

/**
 * Returns an object as required by the ReactSelect custom styles API.
 *
 * @see https://react-select.com/styles
 */
const customStyles = (iconDecoration, size, errorText) => ({
  clearIndicator: provided => ({
    ...provided,
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: sizes[size].buttonPadding,
    paddingTop: '0',
  }),
  container: provided => ({
    ...provided,
    boxSizing: 'border-box',
    fontSize: '12px',
  }),
  control: (provided, { isFocused, isDisabled }) => {
    const hasError = !!errorText;
    return {
      ...provided,
      background: isDisabled ? colors.sand : colors.white,
      borderColor: (isDisabled && colors.sand) || (hasError && colors.copper) || colors.stone,
      boxShadow:
        (hasError && isFocused && `0 0 4px 1px ${colors.copper}`) ||
        (isFocused && `0 0 4px 1px ${rgba(colors.creek, 0.65)}`) ||
        'none',
      '&:hover': {
        borderColor: hasError ? colors.copper : colors.stone,
      },
    };
  },
  dropdownIndicator: (provided, { selectProps: { isSearchable } }) => ({
    ...provided,
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: sizes[size].buttonPadding,
    paddingTop: '0',
    ...(isSearchable
      ? {
          display: 'none',
        }
      : {}),
  }),
  indicatorSeparator: provided => ({
    ...provided,
    display: 'none',
  }),
  menu: provided => ({
    ...provided,
    border: `1px solid ${colors.stone}`,
    borderRadius: '2px',
    boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.15)',
    fontSize: '12px',
  }),
  option: (provided, { isDisabled, isFocused }) => ({
    ...provided,
    backgroundColor: !isDisabled && isFocused ? '#DFF0F0' : null,
    color: colors.charcoal,
    lineHeight: '16px',
    paddingLeft: '11px',
  }),
  valueContainer: provided => ({
    ...provided,
    ...(iconDecoration
      ? {
          backgroundImage: `
            url('data:image/svg+xml;utf8,${encodeURIComponent(`
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="${colors.charcoal}" d="${iconPaths[iconDecoration]}" />
              </svg>
            `)}')
          `,
          backgroundPosition: '10px 50%',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '16px 16px',
          paddingLeft: '32px',
        }
      : {}),
  }),
});

/**
 * An object in the shape required by the ReactSelect replacing components API.
 *
 * @see https://react-select.com/components
 */
const customComponents = {
  // eslint-disable-next-line react/prop-types
  ClearIndicator: ({ onClick, ...props }) => (
    // $FlowFixMe - Flow does not recognize the structure of components from react-select
    <components.ClearIndicator {...props}>
      <ClearButton onClick={onClick} />
    </components.ClearIndicator>
  ),
  DropdownIndicator: props => (
    // $FlowFixMe - Flow does not recognize the structure of components from react-select
    <components.DropdownIndicator {...props}>
      <Icon color="#A7A6A5" icon="caret" size="small" />
    </components.DropdownIndicator>
  ),
};

/**
 * Returns an function that composes the internal theme of the ReactSelect element as required by
 * the ReactSelect custom theme API.
 *
 * @see https://react-select.com/styles#overriding-the-theme
 */
const customTheme = size => reactSelectTheme => ({
  ...reactSelectTheme,
  borderRadius: '2px',
  spacing: {
    ...reactSelectTheme.spacing,
    controlHeight: sizes[size].height || size,
    menuGutter: '4px',
  },
});

type ComboBoxInputProps = {
  errorText: ?string,
  /** Add an icon to the left of the search input; provide the name of the icon from Icon.jsx */
  iconDecoration: $Keys<typeof iconPaths>,
  /** Can be either 'small', 'medium', or any CSS height string, like '40px' */
  size: $Keys<typeof sizes> | string,
};

const ComboBoxInput = ({ errorText, iconDecoration, size, ...props }: ComboBoxInputProps) => (
  <ReactSelect
    {...props}
    components={customComponents}
    styles={customStyles(iconDecoration, size, errorText)}
    theme={customTheme(size)}
  />
);

ComboBoxInput.defaultProps = { size: 'medium' };

export default ComboBoxInput;
